<template>
  <nav
    class="fixed bottom-0 left-0 right-0 bg-white border-t border-border-light z-50 md:hidden safe-area-bottom"
  >
    <div class="flex items-center justify-around h-16 px-2">
      <button
        v-for="item in navItems"
        :key="item.id"
        @click="navigate(item)"
        class="flex flex-col items-center justify-center flex-1 h-full transition-colors relative"
        :class="isActive(item) ? 'text-primary' : 'text-text-secondary'"
      >
        <div
          class="relative"
          :class="item.id === 'cart' ? 'animate-cart-bump' : undefined"
          :key="item.id === 'cart' ? cartBumpTick : item.id"
        >
          <Icon :name="item.icon" :size="24" :weight="isActive(item) ? 'bold' : 'regular'" />
          <span
            v-if="item.badge && item.badge > 0"
            :key="item.id === 'cart' ? `cart-badge-${cartBumpTick}` : `${item.id}-badge`"
            class="absolute -top-1 -right-1 flex items-center justify-center min-w-[16px] h-4 px-1 text-[10px] font-medium text-white bg-red-500 rounded-full"
            :class="item.id === 'cart' ? 'animate-cart-bump' : undefined"
          >
            {{ item.badge > 9 ? '9+' : item.badge }}
          </span>
        </div>
        <span class="text-xs mt-1 font-medium">{{ item.label }}</span>
      </button>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useAppFlow } from '@/composables/core/useAppFlow'
import { useActiveReservation } from '@/composables/features/useActiveReservation'
import { useOrderStore } from '@/stores/order'
import { SMART_DINE_IN_ONLY } from '@/featureFlags'
import Icon from '@/components/atoms/Icon.vue'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const { currentFlow, getFlowRoute, getSmartDineInRoute } = useAppFlow()
const { getStoredActiveReservation } = useActiveReservation()
const orderStore = useOrderStore()
const { cartBumpTick } = storeToRefs(orderStore)

interface NavItem {
  id: string
  label: string
  icon: string
  route: string
  badge?: number
}

const cartBadge = computed(() => orderStore.cartItemCount)
const ordersBadge = computed(() => orderStore.activeOrderItemCount)

const navItems = computed<NavItem[]>(() => {
  const flow = currentFlow.value

  if (flow === 'reserve-table') {
    return [
      {
        id: 'home',
        label: 'Home',
        icon: 'house',
        route: getFlowRoute('/reservations', 'reserve-table'),
      },
      {
        id: 'reservation',
        label: 'Reservation',
        icon: 'calendar-check',
        route: getFlowRoute('/my-reservations', 'reserve-table'),
      },
      {
        id: 'search',
        label: 'Search',
        icon: 'magnifying-glass',
        route: `${getFlowRoute('/reservations', 'reserve-table')}&drawer=reservation-search`,
      },
      {
        id: 'profile',
        label: 'Profile',
        icon: 'user',
        route: getFlowRoute('/profile', 'reserve-table'),
      },
    ]
  }

  if (SMART_DINE_IN_ONLY) {
    return [
      {
        id: 'home',
        label: 'Home',
        icon: 'house',
        route: getFlowRoute('/smart-dine-in', 'smart-dine-in'),
      },
      {
        id: 'orders',
        label: 'Orders',
        icon: 'clock',
        route: getFlowRoute('/my-orders', 'smart-dine-in'),
        badge: ordersBadge.value,
      },
      {
        id: 'cart',
        label: 'Cart',
        icon: 'shopping-cart',
        route: getFlowRoute('/cart', 'smart-dine-in'),
        badge: cartBadge.value,
      },
      {
        id: 'profile',
        label: 'Profile',
        icon: 'user',
        route: getFlowRoute('/profile', 'smart-dine-in'),
      },
    ]
  }

  return [
    {
      id: 'home',
      label: 'Home',
      icon: 'house',
      route:
        flow === 'smart-dine-in'
          ? getFlowRoute('/smart-dine-in', 'smart-dine-in')
          : getFlowRoute('/restaurants', flow),
    },
    {
      id: 'orders',
      label: 'Orders',
      icon: 'clock',
      route: getFlowRoute('/my-orders', flow),
      badge: ordersBadge.value,
    },
    {
      id: 'search',
      label: 'Search',
      icon: 'magnifying-glass',
      route: getFlowRoute('/restaurants', flow) + '&search=true',
    },
    {
      id: 'cart',
      label: 'Cart',
      icon: 'shopping-cart',
      route: getFlowRoute('/cart', flow),
      badge: cartBadge.value,
    },
    {
      id: 'profile',
      label: 'Profile',
      icon: 'user',
      route: getFlowRoute('/profile', flow),
    },
  ]
})

const isActive = (item: NavItem): boolean => {
  const currentPath = route.path
  const itemPath = item.route.split('?')[0]
  const isReservationSearchDrawerOpen = route.query.drawer === 'reservation-search'

  if (currentFlow.value === 'reserve-table') {
    if (item.id === 'home' && currentPath === '/reservations' && !isReservationSearchDrawerOpen) {
      return true
    }

    if (item.id === 'search' && isReservationSearchDrawerOpen) {
      return true
    }
  }

  if (
    currentFlow.value === 'reserve-table' &&
    currentPath === '/reservations' &&
    (item.id === 'home' || item.id === 'search')
  ) {
    return false
  }

  if (currentPath === itemPath) {
    return true
  }

  if (item.id === 'home') {
    if (currentPath === '/' && currentFlow.value !== 'reserve-table') return true
    if (currentFlow.value === 'order-online' && currentPath === '/restaurants') return true
    if (currentPath === '/smart-dine-in') return true
    if (currentFlow.value === 'smart-dine-in' && currentPath.startsWith('/restaurant-menu-view/')) {
      return true
    }
  }

  if (item.id === 'reservation' && currentPath === '/my-reservations') {
    return true
  }

  if (item.id === 'orders' && (currentPath === '/my-orders' || route.query.drawer === 'orders')) {
    return true
  }

  if (item.id === 'cart' && (currentPath === '/cart' || route.query.drawer === 'order')) {
    return true
  }

  if (item.id === 'profile' && currentPath === '/profile') {
    return true
  }

  return false
}

const navigate = (item: NavItem): void => {
  if (item.id === 'search' && currentFlow.value === 'reserve-table') {
    router.push({
      path: '/reservations',
      query: { flow: 'reserve-table', drawer: 'reservation-search' },
    })
    return
  }

  if (item.id === 'home' && currentFlow.value === 'smart-dine-in') {
    if (authStore.isAuthenticated) {
      const activeReservation = getStoredActiveReservation()
      if (activeReservation?.tenantId && activeReservation?.tableNo) {
        router.push(
          getSmartDineInRoute(activeReservation.tenantId, {
            tableNo: activeReservation.tableNo,
            tokenValidate: true,
            ...(activeReservation.reservationId
              ? { reservationId: activeReservation.reservationId }
              : {}),
          })
        )
        return
      }
    }
    router.push(getFlowRoute('/smart-dine-in', 'smart-dine-in'))
    return
  }

  if (item.id === 'cart' && currentFlow.value === 'smart-dine-in') {
    router.push({
      path: route.path,
      query: { ...route.query, drawer: 'order' },
    })
    return
  }

  if (item.id === 'orders' && currentFlow.value === 'smart-dine-in') {
    router.push({
      path: route.path,
      query: { ...route.query, drawer: 'orders' },
    })
    return
  }

  router.push(item.route)
}
</script>

<style scoped>
.safe-area-bottom {
  padding-bottom: env(safe-area-inset-bottom, 0);
}
</style>
